import { FavoriteStar } from "@/components/ui/favorite-star";
import { useEventFollow } from "@/hooks/use-event-follow";
import { cn } from "@/lib/utils";
import { formatMonetaryValue, get } from "@/utils/utils";
import { useCountDown } from "ahooks";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { Clock } from "lucide-react";
import { useMemo } from "react";
import { LiveIcon } from "../ui/live-icon";
// 扩展 dayjs 的 duration 插件
dayjs.extend( duration );
type VolRectProps = {
    eventInfo: Record<string, any>;
    showLiveLabel?: boolean;
    className?: string;
    onCountdownEnd?: () => void;
};
const Star = ( props: any ) => {
    return (
        <svg viewBox="0 0 12 12" fill="none" className={props.className}>
            <g clipPath="url(#clip0_10046_8797)">
                <path
                    d="M6 0C6.56203 3.05084 8.94916 5.43797 12 6C8.94916 6.56203 6.56203 8.94916 6 12C5.43797 8.94916 3.05084 6.56203 0 6C3.05084 5.43797 5.43797 3.05084 6 0Z"
                    fill="#FF950B"
                />
                <path
                    d="M10.0706 0.433594C10.2335 1.31808 10.9256 2.01015 11.8101 2.1731C10.9256 2.33604 10.2335 3.02811 10.0706 3.9126C9.90761 3.02811 9.21554 2.33604 8.33105 2.1731C9.21554 2.01015 9.90761 1.31808 10.0706 0.433594Z"
                    fill="#FF950B"
                />
                <path
                    d="M2.38016 1.12744L2.39116 1.19225C2.47482 1.68499 2.85372 2.07508 3.34381 2.17304C2.85372 2.271 2.47482 2.66109 2.39116 3.15383L2.38016 3.21864L2.36915 3.15383C2.2855 2.66109 1.9066 2.271 1.4165 2.17304C1.9066 2.07508 2.2855 1.68499 2.36915 1.19225L2.38016 1.12744Z"
                    fill="#FF950B"
                />
                <path
                    d="M9.38351 8.8291L9.39588 8.90195C9.48992 9.45583 9.91583 9.89432 10.4667 10.0044C9.91583 10.1146 9.48992 10.553 9.39588 11.1069L9.38351 11.1798L9.37115 11.1069C9.27711 10.553 8.8512 10.1146 8.30029 10.0044C8.8512 9.89432 9.27711 9.45583 9.37115 8.90195L9.38351 8.8291Z"
                    fill="#FF950B"
                />
            </g>
            <defs>
                <clipPath id="clip0_10046_8797">
                    <rect width="12" height="12" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
};

// 格式化剩余时间显示（仅用于24小时内）
function formatRemainingTime( milliseconds: number | undefined ): string | null {
    if ( !milliseconds || milliseconds <= 0 ) return "Ended";

    const duration = dayjs.duration( milliseconds );
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    // 24小时内，显示动态倒计时
    if ( hours > 0 ) return `${hours}h ${minutes}m ${seconds}s`;
    if ( minutes > 0 ) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
}

export default function VolRect( { eventInfo, showLiveLabel, className, onCountdownEnd }: VolRectProps ) {
    const isNew = eventInfo && ( eventInfo.tradeAmount <= 100 );

    // 判断是否为体育事件
    const eventCategory = get( eventInfo, "eventCategory", 0 );
    const isSportEvent = eventCategory !== 0;

    // 根据事件类型选择目标时间
    // 体育事件使用 occurTime，普通事件使用 endTime
    const rawTargetTime = isSportEvent ? get( eventInfo, "occurTime", 0 ) : get( eventInfo, "endTime", 0 );

    // 确保目标时间是时间戳格式（如果是字符串则转换）
    const targetTime = typeof rawTargetTime === "string" ? dayjs( rawTargetTime ).valueOf() : rawTargetTime;

    // 判断是否在24小时内
    const now = Date.now();
    const isWithin24Hours = targetTime > now && ( targetTime - now ) <= 24 * 60 * 60 * 1000;

    // 只有在24小时内才启动倒计时
    const [ remainingTime ] = useCountDown( {
        targetDate: isWithin24Hours ? targetTime : undefined,
        onEnd: () => {
            // 倒计时结束时触发事件
            onCountdownEnd?.();
        },
    } );
    const rectLive = useMemo( () => {
        return eventInfo.liveState === 1;
    }, [ eventInfo ] );

    const { followed, handleLoveClick } = useEventFollow( eventInfo as any );

    return (
        <div
            className={cn(
                "font-medium flex gap-2 items-center text-t3 text-xs border-t border-stroke pt-2 -mx-3 px-4 -mb-1",
                className,
            )}
        >
            <div className="flex items-center gap-2">
                {/* LIVE indicator (only for live mode) */}
                {rectLive && (
                    <div className="flex gap-1.5 items-center">
                        <LiveIcon />
                        {/* LIVE text */}
                        <span className="font-bold text-state-danger leading-5 tracking-[0.13px]">LIVE</span>
                    </div>
                )}
                {isNew
                    ? (
                        <div className="text-[#FF950B] font-medium gap-1 flex items-center">
                            <Star className="size-3" />
                            <span>NEW</span>
                        </div>
                    )
                    : <span>{formatMonetaryValue( eventInfo.tradeAmount )} {" Vol"}</span>}
            </div>
            <div className="ml-auto flex items-center gap-1">
                {/* 只有在24小时内才显示倒计时 */}
                {isWithin24Hours && formatRemainingTime( remainingTime ) && (
                    <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatRemainingTime( remainingTime )}</span>
                    </div>
                )}
                <FavoriteStar className="size-7" active={followed} onClick={handleLoveClick} />
            </div>
        </div>
    );
}
